import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { projectAtom } from '@data/auth';
import { environmentAtom } from '@data/fms/environment/states';
import type { Schedule } from '@data/fms/schedule/types';
import { useNotification } from '@data/notification';
import type { AxiosResponse, AxiosError } from 'axios';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * スケジュールキャンセル指示API
 */
export const useScheduleCancelAPI = (): {
  state: APIState;
  postScheduleCancel: (
    scheduleId: string,
    successfullyMessageType?: 'default' | 'checkPage',
  ) => Promise<Schedule | null>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError, notifySuccess } = useNotification();
  const fmsAPI = useFMSAPI();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const postScheduleCancelAPI = useCallback(
    async (
      projectId: string,
      environmentId: string,
      scheduleId: string,
    ): Promise<Result<Schedule, AxiosResponse>> => {
      try {
        const res = await fmsAPI.post(
          `/${projectId}/environments/${environmentId}/schedules/${scheduleId}/cancel`,
        );
        return new Success(res?.data);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  const postScheduleCancel = useAtomCallback(
    useCallback(
      async (
        get,
        _,
        scheduleId: string,
        successfullyMessageType: 'default' | 'checkPage' = 'default',
      ) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (!project || !environment) {
          notifyError({ message: t('api.fms.post_schedule_cancel.failed') });
          setState('hasError');
          return null;
        }
        setState('loading');
        const res = await postScheduleCancelAPI(
          project!.id,
          environment!.environment_id,
          scheduleId,
        );
        if (!res.value || res.isFailure()) {
          notifyError({
            message: `${t(
              'api.fms.post_schedule_cancel.failed',
            )}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return null;
        }
        notifySuccess({
          message: t(
            `api.fms.post_schedule_cancel.successfully${
              successfullyMessageType === 'default' ? '' : '_check_page'
            }`,
          ),
          testIdAttrs: {
            wrapperTestId: 'scheduleCancel-snackbar',
            closeButtonTestId: 'scheduleCancel-snackbar-close-btn',
          },
        });
        setState('hasValue');
        return res.value;
      },
      [notifyError, notifySuccess, postScheduleCancelAPI, t, getErrorMessage],
    ),
  );

  return {
    state,
    postScheduleCancel,
  };
};
